<template>
  <v-app id="inspire">
    <v-app-bar :color="appBarBg" :style="estiloBarra" elevation="0" height="64" class="app-bar-custom">
      <v-app-bar-nav-icon @click="drawer = !drawer" :color="corSobreMarca" class="ml-1" />

      <div class="bar-brand ml-2">
        <v-img :src="logoBarra" contain height="36" width="36" class="bar-logo" />
        <span class="bar-title" :title="nomeSistema">{{ nomeSistema }}</span>
      </div>

      <v-spacer />

      <!-- Engrenagem de tema -->
      <v-menu location="bottom end" :close-on-content-click="true">
        <template v-slot:activator="{ props }">
          <v-btn icon :color="corSobreMarca" variant="text" class="mr-1" v-bind="props">
            <v-icon>mdi-cog-outline</v-icon>
          </v-btn>
        </template>

        <v-card rounded="lg" width="200" class="pa-1 theme-menu" elevation="8">
          <v-list density="compact" nav>
            <v-list-subheader class="text-caption font-weight-bold">Aparência</v-list-subheader>

            <v-list-item
              rounded="lg"
              :active="currentTheme === 'light'"
              color="primary"
              @click="setTheme('light')"
            >
              <template v-slot:prepend>
                <v-icon size="18">mdi-weather-sunny</v-icon>
              </template>
              <v-list-item-title class="text-body-2">Claro</v-list-item-title>
            </v-list-item>

            <v-list-item
              rounded="lg"
              :active="currentTheme === 'dark'"
              color="primary"
              @click="setTheme('dark')"
            >
              <template v-slot:prepend>
                <v-icon size="18">mdi-weather-night</v-icon>
              </template>
              <v-list-item-title class="text-body-2">Escuro</v-list-item-title>
            </v-list-item>

            <!-- Nome e cor do painel: só para quem pode configurar. -->
            <template v-if="podeConfigurar">
              <v-divider class="my-1" />
              <v-list-item rounded="lg" @click="abrirIdentidade">
                <template v-slot:prepend>
                  <v-icon size="18">mdi-palette-outline</v-icon>
                </template>
                <v-list-item-title class="text-body-2">Identidade do sistema</v-list-item-title>
              </v-list-item>
            </template>
          </v-list>
        </v-card>
      </v-menu>

      <!-- Editor da identidade do painel -->
      <v-dialog v-model="dialogIdentidade" max-width="560">
        <v-card rounded="xl">
          <v-card-title class="d-flex align-center pa-5 pb-2">
            <v-icon color="primary" class="mr-2">mdi-palette-outline</v-icon>
            <span class="text-h6 font-weight-black">Identidade do sistema</span>
          </v-card-title>

          <v-card-text>
            <p class="text-body-2 text-medium-emphasis mb-4">
              Vale para todo o painel e para todas as pessoas. As cores de cada
              evento são configuradas no próprio evento.
            </p>

            <v-text-field
              v-model="formIdentidade.nomeSistema"
              label="Nome do sistema"
              placeholder="AOM Admin"
              variant="outlined"
              density="comfortable"
              maxlength="120"
              counter="120"
              hint="Aparece na barra superior."
              persistent-hint
              class="mb-4"
            />

            <v-text-field
              v-model="formIdentidade.nomeSistemaCurto"
              label="Nome curto"
              placeholder="AOM Admin"
              variant="outlined"
              density="comfortable"
              maxlength="40"
              counter="40"
              hint="Usado no rodapé e em telas estreitas. Em branco, usa o nome completo."
              persistent-hint
              class="mb-4"
            />

            <SeletorCor
              v-model="formIdentidade.corSistema"
              label="Cor da barra superior"
              placeholder="#2563EB"
            />

            <div class="mt-5">
              <span class="text-caption font-weight-bold text-uppercase text-grey-darken-2">
                Logo do sistema
              </span>
              <div class="logo-box mt-2">
                <v-img v-if="previewLogoSistema" :src="previewLogoSistema" height="72" contain />
                <span v-else class="text-caption text-grey">
                  Sem logo própria — o painel usa a logo da AOM
                </span>
              </div>
              <div class="d-flex align-center ga-2 mt-2">
                <v-file-input
                  label="Enviar logo"
                  accept="image/*"
                  variant="outlined"
                  density="compact"
                  prepend-icon=""
                  prepend-inner-icon="mdi-image-outline"
                  hide-details
                  class="flex-grow-1"
                  @update:model-value="carregarLogoSistema"
                />
                <v-btn
                  v-if="previewLogoSistema"
                  variant="text"
                  color="red-darken-2"
                  size="small"
                  @click="removerLogoSistema"
                >
                  Remover
                </v-btn>
              </div>
            </div>
          </v-card-text>

          <v-divider />
          <v-card-actions class="pa-4">
            <v-spacer />
            <v-btn variant="text" @click="dialogIdentidade = false">Cancelar</v-btn>
            <v-btn color="primary" rounded="lg" :loading="salvandoIdentidade" @click="salvarIdentidade">
              Salvar
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <v-btn icon :color="corSobreMarca" variant="text" class="mr-1">
        <v-icon>mdi-bell-outline</v-icon>
      </v-btn>

      <v-btn
        prepend-icon="mdi-logout-variant"
        variant="tonal"
        :color="corSobreMarca"
        size="small"
        class="text-none mr-3 logout-btn"
        rounded="lg"
        @click="handleLogout"
      >
        Sair
      </v-btn>
    </v-app-bar>

    <v-navigation-drawer v-model="drawer" elevation="0" border="0" width="248" :color="drawerBg">
      <BarraLateral />
    </v-navigation-drawer>

    <v-main>
      <v-container fluid class="fill-height pa-0">
        <v-row no-gutters class="fill-height w-100">
          <v-col class="d-flex flex-column">
            <router-view />
          </v-col>
        </v-row>
      </v-container>
    </v-main>

    <!-- Aviso antes do logout por inatividade -->
    <v-dialog v-model="avisoInatividade" persistent max-width="420">
      <v-card rounded="xl" class="pa-2">
        <v-card-title class="d-flex align-center">
          <v-icon color="amber-darken-3" class="mr-2">mdi-clock-alert-outline</v-icon>
          Sessão prestes a expirar
        </v-card-title>
        <v-card-text>
          Você está inativo há um tempo. Por segurança, sua sessão será encerrada em
          <strong>{{ segundosParaLogout }}s</strong>. Deseja continuar conectado?
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-btn variant="text" color="grey-darken-2" @click="handleLogout">Sair agora</v-btn>
          <v-spacer />
          <v-btn color="marca" variant="flat" rounded="lg" @click="continuarConectado">
            Continuar conectado
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script>
import BarraLateral from "@/components/navegacao/BarraLateral.vue";
import logoAOM from "@/assets/LogoAOM.png";
import { getAuth, signOut, onAuthStateChanged } from "firebase/auth";
import DoisFatoresService from "@/service/DoisFatoresService";
import ConfiguracaoSistemaService from "@/service/ConfiguracaoSistemaService";
import SeletorCor from "@/components/inputs/SeletorCor.vue";
import { PERMISSOES } from "@/util/permissoes";
import { aplicarCorNoTema, contrasteSobre, COR_MARCA_PADRAO } from "@/util/corMarca";

// Eventos que contam como "atividade" do usuário.
const EVENTOS_ATIVIDADE = ["mousemove", "mousedown", "keydown", "scroll", "touchstart", "click"];

export default {
  name: "LayoutPadrao",
  components: { BarraLateral, SeletorCor },
  data: () => ({
    drawer: true,
    logoAOM,
    currentTheme: localStorage.getItem("aom-theme") || "light",
    // Inatividade
    avisoInatividade: false,
    segundosParaLogout: 60,
    timerAviso: null,
    timerLogout: null,
    intervaloContagem: null,
    aoAgir: null,
    // Identidade do painel
    dialogIdentidade: false,
    salvandoIdentidade: false,
    formIdentidade: { nomeSistema: "", nomeSistemaCurto: "", corSistema: "", logoBase64: "", logoTipo: "" },
  }),
  computed: {
    isDark() {
      return this.currentTheme === "dark";
    },
    nomeSistema() {
      return this.$store.getters.nomeSistema;
    },

    /** Logo da barra: a da instituição quando houver, senão a da AOM. */
    logoBarra() {
      return this.$store.getters.logoSistema || logoAOM;
    },

    /** Pré-visualização dentro do diálogo, a partir do que está no formulário. */
    previewLogoSistema() {
      if (!this.formIdentidade.logoBase64) return null;
      return `data:${this.formIdentidade.logoTipo || "image/png"};base64,${this.formIdentidade.logoBase64}`;
    },
    podeConfigurar() {
      return this.$store.getters.hasPermission(PERMISSOES.EDITAR_CONFIGURACOES);
    },
    /**
     * Cor do texto e dos ícones sobre a barra. Calculada pela luminância da
     * cor escolhida: uma cor clara com texto branco ficaria ilegível, e o
     * administrador pode escolher qualquer cor.
     */
    corSobreMarca() {
      if (this.isDark) return "#FFFFFF";
      return contrasteSobre(this.$store.getters.corSistema || COR_MARCA_PADRAO);
    },

    /** Publica a cor de contraste para o CSS do título da barra. */
    estiloBarra() {
      return { "--cor-sobre-marca": this.corSobreMarca };
    },

    // Barra e gaveta usam os tokens da marca: assim seguem a cor do sistema
    // pelo mesmo caminho que o resto das telas, em vez de por regra própria.
    appBarBg() {
      return this.isDark ? "#0f1c35" : "marca";
    },
    drawerBg() {
      return this.isDark ? "#0f1c35" : "marca-tenue";
    },
  },
  created() {
    this.verificarAutenticacao();
  },
  async mounted() {
    this.iniciarMonitorInatividade();
    // Carrega a identidade e repinta o tema com a cor da instituicao.
    await this.$store.dispatch("carregarConfigSistema");
    this.aplicarCorDaMarca();
  },
  beforeUnmount() {
    this.pararMonitorInatividade();
  },
  methods: {
    /**
     * Repinta os tokens da marca no tema do Vuetify. Como as telas usam
     * `marca`, `marca-clara` e afins em vez de cores fixas, uma chamada aqui
     * troca a cor do painel inteiro — barra, gaveta, títulos e botões.
     */
    aplicarCorDaMarca() {
      const cor = this.$store.getters.corSistema || COR_MARCA_PADRAO;
      aplicarCorNoTema(this.$vuetify.theme, cor);
    },

    abrirIdentidade() {
      const config = this.$store.state.configSistema || {};
      this.formIdentidade = {
        nomeSistema: config.nomeSistema || "",
        nomeSistemaCurto: config.nomeSistemaCurto || "",
        corSistema: config.corSistema || "",
        logoBase64: config.logoBase64 || "",
        logoTipo: config.logoTipo || "",
      };
      this.dialogIdentidade = true;
    },

    carregarLogoSistema(arquivo) {
      const file = Array.isArray(arquivo) ? arquivo[0] : arquivo;
      if (!file) return;
      const leitor = new FileReader();
      leitor.onload = () => {
        // O backend guarda o base64 puro e o tipo em colunas separadas.
        this.formIdentidade.logoBase64 = String(leitor.result).split(",")[1];
        this.formIdentidade.logoTipo = file.type;
      };
      leitor.readAsDataURL(file);
    },

    /**
     * Envia string vazia, e não nulo: o backend só mexe na logo quando o campo
     * vem no corpo, e é assim que ele distingue "apagar" de "não alterar".
     */
    removerLogoSistema() {
      this.formIdentidade.logoBase64 = "";
      this.formIdentidade.logoTipo = "";
    },

    async salvarIdentidade() {
      this.salvandoIdentidade = true;
      try {
        const { data } = await ConfiguracaoSistemaService.update(this.formIdentidade);
        // Comita a resposta do servidor, e não o formulário: campos em branco
        // voltam ao padrão no backend, e a barra precisa refletir isso.
        this.$store.commit("SET_CONFIG_SISTEMA", data);
        // Repinta na hora, sem exigir recarregar a página.
        this.aplicarCorDaMarca();
        this.dialogIdentidade = false;
      } catch (e) {
        console.error("Erro ao salvar a identidade do sistema:", e);
      } finally {
        this.salvandoIdentidade = false;
      }
    },

    setTheme(theme) {
      this.currentTheme = theme;
      this.$vuetify.theme.global.name = theme;
      localStorage.setItem("aom-theme", theme);
    },

    // --- Monitor de inatividade (1h por padrão, com aviso 1 min antes) ---
    limiteInatividadeMs() {
      const minutos = parseInt(localStorage.getItem("inatividadeMinutos"), 10) || 60;
      return minutos * 60 * 1000;
    },
    iniciarMonitorInatividade() {
      this.aoAgir = () => this.registrarAtividade();
      EVENTOS_ATIVIDADE.forEach((evt) =>
        window.addEventListener(evt, this.aoAgir, { passive: true })
      );
      this.agendarTimers();
    },
    pararMonitorInatividade() {
      if (this.aoAgir) {
        EVENTOS_ATIVIDADE.forEach((evt) => window.removeEventListener(evt, this.aoAgir));
      }
      this.limparTimers();
    },
    limparTimers() {
      clearTimeout(this.timerAviso);
      clearTimeout(this.timerLogout);
      clearInterval(this.intervaloContagem);
    },
    agendarTimers() {
      this.limparTimers();
      const limite = this.limiteInatividadeMs();
      const avisoAntesMs = 60 * 1000; // avisa 1 min antes
      // Mostra o aviso 1 min antes do fim.
      this.timerAviso = setTimeout(() => this.mostrarAviso(), Math.max(limite - avisoAntesMs, 0));
      // Logout no fim do limite.
      this.timerLogout = setTimeout(() => this.handleLogout(), limite);
    },
    registrarAtividade() {
      // Enquanto o aviso está aberto, ignoramos atividade passiva: o usuário
      // precisa clicar em "Continuar" (que também renova a sessão no servidor).
      if (this.avisoInatividade) return;
      this.agendarTimers();
    },
    mostrarAviso() {
      this.segundosParaLogout = 60;
      this.avisoInatividade = true;
      this.intervaloContagem = setInterval(() => {
        this.segundosParaLogout -= 1;
        if (this.segundosParaLogout <= 0) {
          clearInterval(this.intervaloContagem);
        }
      }, 1000);
    },
    async continuarConectado() {
      this.avisoInatividade = false;
      clearInterval(this.intervaloContagem);
      // Renova também a sessão no backend (o filtro atualiza a última atividade).
      try {
        await DoisFatoresService.status();
      } catch (e) {
        // Se já expirou no servidor, o interceptor redireciona para o 2FA.
        console.warn("Sessão não pôde ser renovada", e);
      }
      this.agendarTimers();
    },
    verificarAutenticacao() {
      const auth = getAuth();
      onAuthStateChanged(auth, async (user) => {
        if (!user) {
          this.limparSessaoERedirecionar();
        } else {
          const token = await user.getIdToken(true);
          localStorage.setItem("idToken", token);
        }
      });
    },
    async handleLogout() {
      this.limparTimers();
      this.avisoInatividade = false;
      try {
        // Encerra a sessão 2FA no servidor (melhor esforço).
        await DoisFatoresService.logout();
      } catch (e) {
        // ignora: pode já estar expirada
      }
      try {
        const auth = getAuth();
        await signOut(auth);
      } catch (error) {
        console.error("Erro ao deslogar:", error);
      }
      this.limparSessaoERedirecionar();
    },
    limparSessaoERedirecionar() {
      localStorage.removeItem("idToken");
      localStorage.removeItem("lastRoute");
      localStorage.removeItem("userPermissions");
      localStorage.removeItem("accessUser");
      DoisFatoresService.limparSessao();
      if (this.$store) this.$store.dispatch("logout");
      this.$router.push("/login");
    },
  },
};
</script>

<style scoped>
.app-bar-custom {
  border-bottom: 1px solid rgba(255, 255, 255, 0.12) !important;
}

.bar-brand {
  display: flex;
  align-items: center;
  gap: 10px;
}

.bar-logo {
  border-radius: 8px;
  flex-shrink: 0;
}

.logo-box {
  border: 1px dashed rgba(0, 0, 0, 0.2);
  border-radius: 10px;
  min-height: 84px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 6px;
  background: rgba(0, 0, 0, 0.015);
}

.bar-title {
  font-size: 1.05rem;
  font-weight: 700;
  color: var(--cor-sobre-marca, #fff);
  letter-spacing: 0.01em;
  font-family: 'Poppins', Roboto, sans-serif;
}

.logout-btn {
  border: 1px solid rgba(255,255,255,0.18) !important;
}


.fill-height {
  min-height: calc(100vh - 64px);
}

/*
 * O conteúdo da gaveta precisa ser um flex de altura definida para que a lista
 * de menus role por dentro dela. Sem isto, com vários grupos abertos a lista
 * crescia além da tela e os últimos itens ficavam inalcançáveis.
 */
:deep(.v-navigation-drawer__content) {
  display: flex;
  flex-direction: column;
  height: 100%;
  overflow: hidden;
}
</style>
